import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { getTrans } from 'modules/i18n';
import { FormBlockInstance } from '../api-types';
import { textFor } from '../localizedText';
import {
  signalHeading,
  signalHelp,
  signalHint,
  signalInstruction
} from '../signalStyles';
import { questionIdOf } from './types';

/**
 * The words above every block's control: question, help, instruction, hint, and whether an answer is
 * expected at all.
 *
 * Shared rather than repeated because it was repeated, eleven times, and the copies had already
 * drifted — one block styled its help as a `body2` caption, and none of them rendered `instruction`
 * at all, so an operator could write a line of guidance in the builder, watch it appear in the
 * preview, and have it silently dropped for the person it was written for.
 *
 * FOUR LINES, FOUR AUTHORS, and the order is the point:
 *
 *   - `question` is what is being asked.
 *   - `help` is what the question MEANS, in the operator's words.
 *   - `instruction` is how to answer it, also in the operator's words. pro-core gives these two
 *     separate fields, so collapsing them here would lose the distinction an operator was given a
 *     field to express.
 *   - `hint` is DERIVED from the configuration — the form telling a respondent what kind of answer
 *     the control takes. It sits last and quietest because it is the only one that is not somebody's
 *     writing, and it must not compete with the ones that are.
 *
 * The hint is passed IN rather than computed here. Only the block knows what its own type needs
 * said, and a switch on `block.type` in this file would be a second dispatch table beside the
 * registry — the thing the registry exists so that nobody writes.
 */
export const BlockHeading = ({
  block,
  locale,
  hint,
  showOptional = true
}: {
  block: FormBlockInstance;
  locale: string;
  /** What kind of answer the control takes, derived by the block from its own config. */
  hint?: string;
  /**
   * Whether to say "Optional" when the block is not required.
   *
   * False for a CONTENT block: it collects nothing, so requiredness is meaningless there — pro-core
   * refuses `required: true` on one — and announcing "Optional" would offer a choice nobody has.
   */
  showOptional?: boolean;
}) => {
  const question = textFor(block.content?.question, locale);
  const help = textFor(block.content?.help, locale);
  const instruction = textFor(block.content?.instruction, locale);

  return (
    <Stack spacing={1}>
      <Typography
        id={questionIdOf(block)}
        variant="h5"
        color="text.primary"
        sx={signalHeading}
      >
        {question}
      </Typography>
      {help && <Typography sx={signalHelp}>{help}</Typography>}
      {instruction && (
        <Typography sx={signalInstruction}>{instruction}</Typography>
      )}
      {hint && <Typography sx={signalHint}>{hint}</Typography>}
      {showOptional && !block.required && (
        <Typography variant="caption" color="text.disabled">
          {getTrans('guestForm.optional')}
        </Typography>
      )}
    </Stack>
  );
};
